import { Box, IconButton, Stack, styled } from '@mui/material';
import { ContactIcon, ContactNotFoundIcon, LockIconNew } from '@synergy/theme';
import { PoolPopover, PoolSpinner } from '@synergy/components';
import { shouldForwardPropExclude } from '@synergy/utils';
import { useRef, useState } from 'react';

import { Contact } from '../../models/contact';
import { ContactDetailsContent } from './contact-details-content';
import { fetchContactInfo } from '../../actions/contact-actions';
import { localesSelector } from '../../selectors/locale-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export interface PoolContactInformationProps {
    contactId: number;
    accountId?: number;
    showTopics?: boolean;
}

const AdornmentIcon = ({ contact }: { contact: Contact | null }) => {
    if (contact === null) {
        return <ContactNotFoundIcon htmlColor="currentColor" />;
    }
    if (contact?.isLocked) {
        return <LockIconNew htmlColor="currentColor" />;
    }

    return <ContactIcon />;
};

const StyledContainer = styled(Stack)(() => ({
    minWidth: 250,

    '& label': {
        fontSize: 11,
        color: 'rgba(33, 39, 46, 0.6)',
        marginBottom: 3,
    },

    '& > p:not(:last-child)': {
        marginBottom: 18.5,
    },
}));

const StyledIconButton = styled(IconButton, { shouldForwardProp: shouldForwardPropExclude('isOpen') })<{
    isOpen: boolean;
}>(({ theme, isOpen }) => ({
    '& > span:first-child': {
        position: 'relative',
    },
    ...(isOpen && {
        background: 'none',

        '& svg': {
            color: theme.palette.primary.main,
        },
    }),
}));

export const ContactDetails = (props: PoolContactInformationProps) => {
    const dispatch = useThunkDispatch();
    const buttonRef = useRef<HTMLDivElement>(null);
    const [isOpen, setOpen] = useState<boolean>(false);
    const { contactDetails, contactDetailsLoading } = useAppSelector(state => state.contacts);
    const locales = useAppSelector(localesSelector);

    const contactDetail = contactDetails[props.contactId];

    const handleOpen = () => {
        if (!contactDetail) {
            dispatch(fetchContactInfo(props.contactId, props.accountId, props.showTopics));
        }
        setOpen(true);
    };

    return (
        <>
            <StyledIconButton onClick={handleOpen} isOpen={isOpen}>
                <Box ref={buttonRef} sx={{ position: 'absolute', top: -30 }} />
                <AdornmentIcon contact={contactDetail} />
            </StyledIconButton>
            <PoolPopover open={isOpen} onClose={() => setOpen(false)} anchorEl={buttonRef.current}>
                <PoolSpinner enabled={!!contactDetailsLoading} sx={{ width: '100%' }}>
                    <StyledContainer>
                        <ContactDetailsContent
                            contactDetail={contactDetail}
                            isLoading={!!contactDetailsLoading}
                            locales={locales}
                        />
                    </StyledContainer>
                </PoolSpinner>
            </PoolPopover>
        </>
    );
};
